import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, CircularProgress, Alert, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import { CloudUpload, CloudDownload, RestorePage, Refresh } from '@mui/icons-material';
import { api } from '../../services/api';

interface BackupFile {
  filename: string;
  sizeBytes: number;
  sizeMb: number;
  lastModified: string;
}

const BackupRestore: React.FC = () => {
  const [files, setFiles] = useState<BackupFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [restoreDialog, setRestoreDialog] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  const fetchBackups = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/backups');
      setFiles(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setError('Failed to load backups.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const handleCreate = async () => {
    setCreating(true); setError(''); setSuccess('');
    try {
      const res = await api.post('/backups/create');
      setSuccess(res.data?.message || 'Backup created.');
      await fetchBackups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create backup.');
    } finally { setCreating(false); }
  };

  const handleDownload = async (filename: string) => {
    try {
      const res = await api.get(`/backups/${filename}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to download backup.');
    }
  };

  const handleRestore = async () => {
    if (!restoreDialog) return;
    setRestoring(true); setError(''); setSuccess('');
    try {
      const res = await api.post('/backups/restore', { filename: restoreDialog });
      setSuccess(res.data?.message || 'Database restored.');
      setRestoreDialog(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to restore.');
    } finally { setRestoring(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Backup &amp; Restore</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchBackups} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<CloudUpload />} onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : 'Create Backup'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {creating && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
          Creating backup, please wait...
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Filename</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Size</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No backups found. Click "Create Backup" to create one.</Typography>
                  </TableCell>
                </TableRow>
              ) : files.map((f) => (
                <TableRow key={f.filename} hover>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{f.filename}</Typography></TableCell>
                  <TableCell>{f.lastModified}</TableCell>
                  <TableCell align="right">{f.sizeMb} MB</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleDownload(f.filename)} title="Download">
                      <CloudDownload />
                    </IconButton>
                    <IconButton size="small" color="warning" onClick={() => setRestoreDialog(f.filename)} title="Restore">
                      <RestorePage />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!restoreDialog} onClose={() => !restoring && setRestoreDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore Database</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <strong>Warning:</strong> This will replace the entire database with the backup
            "<strong>{restoreDialog}</strong>". All current data will be lost. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(null)} disabled={restoring}>Cancel</Button>
          <Button onClick={handleRestore} variant="contained" color="error" disabled={restoring}>
            {restoring ? 'Restoring...' : 'Restore Database'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BackupRestore;
